import type { MeetingSamenvatting } from '@vip/shared';
import { datum } from './gesprekstand';

/**
 * Zoeken en groeperen in de eigen gesprekken.
 *
 * Beide gebeuren in de browser en niet op de server. De lijst komt al in één
 * antwoord binnen, dus er is niets te halen: filteren op wat er al staat is
 * onmiddellijk en kost geen enkele uitvraag. Zodra die lijst gepagineerd wordt
 * moet het zoeken mee naar de server, want dan zoek je anders alleen in de
 * pagina die je toevallig binnen hebt.
 */

/**
 * Waar een gesprek op te vinden is.
 *
 * De datum staat er in dezelfde vorm in als op de kaart, en niet alleen als
 * ISO-waarde. Anders kun je niet vinden wat je leest: op de kaart staat
 * "24 augustus 2026", en dan moet "augustus" ook werken.
 *
 * De naam telt mee, maar kan het zoeken niet dragen: hij is optioneel, en een
 * gesprek zonder naam valt op de kaart terug op zijn datum. Dan moet je daar ook
 * op kunnen zoeken.
 */
function zoektekst(g: MeetingSamenvatting): string {
  const d = new Date(g.occurredAt);
  return [
    g.titel ?? '',
    // "24 augustus 2026", letterlijk wat de kaart toont.
    datum(g.occurredAt),
    // "2026-08-24" en "24-8-2026": wie een datum typt doet dat zelden voluit.
    g.occurredAt.slice(0, 10),
    `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`,
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Alle getypte woorden moeten voorkomen, in willekeurige volgorde. Zo werkt
 * "augustus jamie" ook, en dat is precies hoe je een gesprek terughaalt dat je
 * half herinnert: een naam en een maand.
 */
export function filter(
  gesprekken: readonly MeetingSamenvatting[],
  zoekterm: string,
): MeetingSamenvatting[] {
  const woorden = zoekterm.toLowerCase().split(/\s+/).filter(Boolean);
  if (woorden.length === 0) return [...gesprekken];
  return gesprekken.filter((g) => {
    const tekst = zoektekst(g);
    return woorden.every((w) => tekst.includes(w));
  });
}

export interface Jaargroep {
  /** "2026", zowel het kopje als de waarde van het tabje. */
  jaar: string;
  gesprekken: MeetingSamenvatting[];
}

/**
 * Per jaar, nieuwste eerst.
 *
 * Per jaar en niet per maand: er worden in de praktijk maximaal een twaalftal
 * gesprekken per jaar opgenomen, dus een jaar is precies één overzichtelijke
 * lijst. Hier stond een maandgroepering, gebouwd op de 141 gesprekken over
 * twintig maanden die in de ontwikkeldatabase staan. Die verhouding komt uit een
 * seedscript en niet uit het gebruik.
 *
 * Het jaartal wordt uit de ISO-tekst geknipt en niet uit een `Date` gelezen: in
 * een tijdzone achter UTC schuift 1 januari 00:00Z naar 31 december, en dan valt
 * een gesprek in het verkeerde jaar.
 */
export function groepeerPerJaar(gesprekken: readonly MeetingSamenvatting[]): Jaargroep[] {
  const per = new Map<string, MeetingSamenvatting[]>();
  for (const g of gesprekken) {
    const jaar = g.occurredAt.slice(0, 4);
    const groep = per.get(jaar);
    if (groep) groep.push(g);
    else per.set(jaar, [g]);
  }

  return [...per.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([jaar, rijen]) => ({ jaar, gesprekken: rijen }));
}
